import { ReactNode } from "react"

import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip"

import { cn } from "@/lib/utils"

function TooltipProvider({ delay = 100, ...props }: TooltipPrimitive.Provider.Props) {
  return (
    <TooltipPrimitive.Provider data-slot="tooltip-provider" delay={delay} {...props} />
  )
}

function Tooltip({
  children,
  content,
  defaultOpen,
  open,
  onOpenChange,
  onOpenChangeComplete,
  disableHoverablePopup = false,
  delay = 100,
  side,
  className,
  ...props
}: TooltipPrimitive.Root.Props &
  Omit<TooltipPrimitive.Popup.Props, "content"> & {
    children: TooltipPrimitive.Trigger.Props["render"]
    side?: TooltipPrimitive.Positioner.Props["side"]
    delay?: number
    content: ReactNode
  }) {
  if (!content) return <>{children}</>

  return (
    <TooltipPrimitive.Root
      data-slot="tooltip"
      defaultOpen={defaultOpen}
      open={open}
      onOpenChange={onOpenChange}
      onOpenChangeComplete={onOpenChangeComplete}
      disableHoverablePopup={disableHoverablePopup}
    >
      <TooltipPrimitive.Trigger render={children} delay={delay} />
      <TooltipPositioner collisionPadding={8} sideOffset={8} side={side}>
        <TooltipContent className={className} {...props}>
          {content}
          <TooltipArrow />
        </TooltipContent>
      </TooltipPositioner>
    </TooltipPrimitive.Root>
  )
}

function TooltipTrigger({ ...props }: TooltipPrimitive.Trigger.Props) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

function TooltipPositioner({ className, ...props }: TooltipPrimitive.Positioner.Props) {
  return (
    <TooltipPrimitive.Portal container={document.body}>
      <TooltipPrimitive.Positioner
        data-slot="tooltip-positioner"
        sideOffset={8}
        className={cn("z-300", className)}
        {...props}
      />
    </TooltipPrimitive.Portal>
  )
}

function TooltipContent({
  className,
  children,
  ...props
}: TooltipPrimitive.Popup.Props) {
  return (
    <TooltipPrimitive.Popup
      data-slot="tooltip-content"
      className={cn(
        "z-50 w-fit origin-(--transform-origin) animate-in rounded-md bg-tooltip px-3 py-1.5 text-xs text-balance text-tooltip-foreground fade-in-0 zoom-in-95 data-[closed]:animate-out data-[closed]:fade-out-0 data-[closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...props}
    >
      {children}
      <TooltipArrow />
    </TooltipPrimitive.Popup>
  )
}

function TooltipArrow({ className, ...props }: TooltipPrimitive.Arrow.Props) {
  return (
    <TooltipPrimitive.Arrow
      data-slot="tooltip-arrow"
      className={cn(
        "z-50 size-2 rotate-45 rounded-[2px] bg-tooltip fill-primary",
        "data-[side=bottom]:top-px data-[side=bottom]:-translate-y-1/2",
        "data-[side=top]:bottom-px data-[side=top]:translate-y-1/2",
        "data-[side=left]:right-px data-[side=left]:translate-x-1/2",
        "data-[side=right]:left-px data-[side=right]:-translate-x-1/2",
        className,
      )}
      {...props}
    />
  )
}

type TooltipProps = React.ComponentProps<typeof Tooltip>

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipPositioner,
  type TooltipProps,
}
